import { documentsBaseKey } from "@common/api/documents"
import { makeAxiosMutation, makeQuery } from "@common/api/utils"
import {
  type AttachmentPublic,
  type DocumentPublic,
  TraceAttachmentService,
} from "@common/client"
import { useTrace } from "@common/hooks/trace-provider"
import { toast } from "@common/hooks/use-toast"
import { useTrackEvent } from "@common/hooks/use-track-event"
import { getErrorMessage } from "@common/lib/error-utils"
import { useCallback, useState } from "react"

export const useUploadTraceAttachmentMutation = makeAxiosMutation({
  serviceCall: ({ traceId, file }: { traceId: string; file: File }) =>
    TraceAttachmentService.uploadAttachment({
      trace_id: traceId,
      bodyTraceAttachmentUploadAttachment: { file },
    }),
  invalidates: () => [documentsBaseKey],
})

export const useAttachExistingDocumentsMutation = makeAxiosMutation({
  serviceCall: TraceAttachmentService.attachExistingDocuments,
  invalidates: () => [documentsBaseKey],
})

export const sandboxAttachmentKey = (
  conversationSessionId: string,
  filePath: string,
) => ["sandbox-attachment", conversationSessionId, filePath] as const

export const sandboxAttachmentBlobQuery = makeQuery({
  serviceCall: ({
    conversationSessionId,
    filePath,
  }: {
    conversationSessionId: string
    filePath: string
  }) =>
    TraceAttachmentService.readSandboxAttachment({
      conversation_session_id: conversationSessionId,
      file_path: filePath,
    }),
  queryKey: ({ conversationSessionId, filePath }) =>
    sandboxAttachmentKey(conversationSessionId, filePath),
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
})

export type PendingAttachment =
  | {
      tempId: string
      kind: "upload"
      file: File
      status: "uploading" | "done" | "error"
      attachment?: AttachmentPublic
      error?: string
    }
  | {
      tempId: string
      kind: "existing"
      document: DocumentPublic
      status: "attaching" | "done" | "error"
      attachment?: AttachmentPublic
      error?: string
    }

export interface TraceUpload {
  pending: PendingAttachment[]
  uploadFiles: (files: File[]) => Promise<void>
  attachExisting: (documents: DocumentPublic[]) => Promise<void>
  remove: (tempId: string) => void
  clear: () => void
  isUploading: boolean
}

// Owns the composer's pending-attachment list: files are uploaded (or existing
// documents attached) to the trace as they're added (showing per-item progress) but
// no message is sent — the composer gathers the finished attachments and sends them
// with the user's own prompt.
export function useTraceFileUpload(): TraceUpload {
  const trackEvent = useTrackEvent()
  const { ensureTrace } = useTrace()
  const uploadMutation = useUploadTraceAttachmentMutation()
  const attachExistingMutation = useAttachExistingDocumentsMutation()
  const [pending, setPending] = useState<PendingAttachment[]>([])

  const patch = useCallback(
    (
      tempId: string,
      next:
        | { status: "done"; attachment: AttachmentPublic }
        | { status: "error"; error: string },
    ) =>
      setPending((prev) =>
        prev.map(
          (p): PendingAttachment =>
            p.tempId === tempId ? { ...p, ...next } : p,
        ),
      ),
    [],
  )

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return
      const items = files.map((file) => ({ tempId: crypto.randomUUID(), file }))
      setPending((prev) => [
        ...prev,
        ...items.map(
          ({ tempId, file }): PendingAttachment => ({
            tempId,
            kind: "upload",
            file,
            status: "uploading",
          }),
        ),
      ])
      const traceId = await ensureTrace("attachment")
      await Promise.all(
        items.map(async ({ tempId, file }) => {
          try {
            const attachment = await uploadMutation.mutateAsync({
              traceId,
              file,
            })
            trackEvent("trace_attachment_uploaded", {})
            patch(tempId, { status: "done", attachment })
          } catch (e) {
            trackEvent("trace_attachment_error", {})
            const message = getErrorMessage(e, "Upload failed")
            patch(tempId, { status: "error", error: message })
            toast({
              title: "Upload failed",
              description: message,
              variant: "destructive",
            })
          }
        }),
      )
    },
    [ensureTrace, uploadMutation, patch, trackEvent],
  )

  const attachExisting = useCallback(
    async (documents: DocumentPublic[]) => {
      if (documents.length === 0) return
      const items = documents.map((document) => ({
        tempId: crypto.randomUUID(),
        document,
      }))
      setPending((prev) => [
        ...prev,
        ...items.map(
          ({ tempId, document }): PendingAttachment => ({
            tempId,
            kind: "existing",
            document,
            status: "attaching",
          }),
        ),
      ])
      const traceId = await ensureTrace("attachment")
      attachExistingMutation.mutate(
        {
          trace_id: traceId,
          attachExistingDocuments: {
            document_ids: items.map((item) => item.document.id),
          },
        },
        {
          onSuccess: (attachments) => {
            trackEvent("trace_attachment_existing_attached", {})
            for (const { tempId, document } of items) {
              const attachment = attachments.find(
                (a) => a.document_id === document.id,
              )
              patch(
                tempId,
                attachment
                  ? { status: "done", attachment }
                  : {
                      status: "error",
                      error: "Document is no longer accessible",
                    },
              )
            }
          },
          onError: (e) => {
            trackEvent("trace_attachment_existing_error", {})
            const error = getErrorMessage(e, "Attach failed")
            for (const { tempId } of items) {
              patch(tempId, { status: "error", error })
            }
            toast({
              title: "Attach failed",
              description: error,
              variant: "destructive",
            })
          },
        },
      )
    },
    [ensureTrace, attachExistingMutation, patch, trackEvent],
  )

  const remove = useCallback(
    (tempId: string) =>
      setPending((prev) => prev.filter((p) => p.tempId !== tempId)),
    [],
  )

  const clear = useCallback(() => setPending([]), [])

  const isUploading = pending.some(
    (p) => p.status === "uploading" || p.status === "attaching",
  )

  return {
    pending,
    uploadFiles,
    attachExisting,
    remove,
    clear,
    isUploading,
  }
}
